import { IACSendingInformation } from './application/baseApplication';
import { IACTransport } from './transport/iacTransport';
import { IACUsecase, IACUsecaseConfig } from './iacType/iacUsecase';
import { IACEvent } from './iacType/iacEvent';
import { IACMessage } from './iacType/iacMessage';

import { IACMessageData, IACProtocol } from './transport/iacProtocol';
import { TYPES } from './constants';

import { BaseApplication } from './application';

import { Config, IACEndpoint, IACHub } from '.';
import * as Logger from '@navigator/frontend-logger';

export interface CommunicationConfig {
  appname: string;
  context_token: string;
  usecases: {
    [key: string]: IACUsecaseConfig;
  };
}

let config: any;
let transport: IACTransport;
const messageQueue: {
  [key: string]: IACMessage[];
} = {};

export class IACCommunication {

  public static init(): void {
    config = Config.get().communication;

    transport = new IACTransport();
    transport.onMessage = (message: IACUsecase) => this.onMessage(message);
    transport.init();
  }

  private static onMessage(message: IACUsecase) {
    if (!(message instanceof IACUsecase)) {
      Logger.warn('Received an IAC message which is not a valid IACUsecase object.'
      + 'IAC events are only usable for simplex communication; Navigator to apps.');
      return;
    }

    IACHub.handle(message);
  }

  public static createUsecaseFromConfig(usecaseData: IACMessageData): IACUsecase {
    return transport.incomingMessageFactory(usecaseData) as IACUsecase;
  }

  public static invokeStartupUsecase(usecaseData: IACMessageData): void {
    IACHub.handle(this.createUsecaseFromConfig(usecaseData));
  }

  public static createUsecase(name: string, payload: any = {}): IACUsecase {
    const iacUsecase = new IACUsecase();

    iacUsecase.protocol = new IACProtocol();
    iacUsecase.transport = transport;

    iacUsecase.protocol.setFlags(TYPES.USECASE | TYPES.RPC | TYPES.INTERN);
    iacUsecase.protocol.setSender(IACEndpoint.SYSNAME);

    iacUsecase.setName(name);
    iacUsecase.setPayload(payload);

    // This message is a bit special
    // because the navigator supports a config object regarding the use usecase.
    iacUsecase.config = Config.get().communication.usecases[name];

    return iacUsecase;
  }

  public static hasUsecase(name: string): boolean {
    return Object.prototype.hasOwnProperty.call(config.usecases, name);
  }

  public static createEvent(name: string, payload: any = {}): IACEvent {
    const iacEvent = new IACEvent();

    iacEvent.protocol = new IACProtocol();
    iacEvent.transport = transport;

    iacEvent.protocol.setFlags(TYPES.EVENT | TYPES.RPE | TYPES.INTERN);
    iacEvent.protocol.setSender(IACEndpoint.SYSNAME);

    iacEvent.setName(name);

    const tmpPayload = payload;
    tmpPayload.type = name;
    tmpPayload.timeStamp = new Date().getTime();

    iacEvent.setPayload(tmpPayload);

    return iacEvent;
  }

  public static sendMessage(iacMessage: IACMessage, receiverApp: BaseApplication): void {
    if (!(iacMessage instanceof IACMessage)) {
      throw new Error('Cannot send a message which is not an instance of IACMessage');
    }

    if (!(receiverApp instanceof BaseApplication)) {
      throw new Error('The receiver application must be a instance of BaseApplication');
    }

    /* istanbul ignore next else */
    if ((iacMessage instanceof IACUsecase)) {
      if (!iacMessage.validate()) {
        Logger.error(`IACCommunication.sendMessage: Message ${iacMessage.getName()} sent from 
        ${iacMessage.getSender()} to ${receiverApp.getSysname()} failed validation!`);
        return;
      }
    }

    if (receiverApp.isIacConnected() && receiverApp.isRunning()) {
      // The application is yet connected. We'll send the message immediately.
      const sendingInformation: IACSendingInformation = receiverApp.getIACSendingInformation();
      /* istanbul ignore next else */
      if (sendingInformation) {
        iacMessage.send(sendingInformation);
      }
    } else {
      this.queueMessage(iacMessage, receiverApp);
    }
  }

  private static queueMessage(iacMessage: IACMessage, receiverApp: BaseApplication) {
    if (!(iacMessage instanceof IACMessage)) {
      throw new Error('Cannot queue a message which is not an instance of IACMessage');
    }

    if (!(receiverApp instanceof BaseApplication)) {
      throw new Error('The receiver application must be a instance of BaseApplication');
    }

    /* istanbul ignore next else */
    if (!Object.prototype.hasOwnProperty.call(messageQueue, receiverApp.getSysname())) {
      messageQueue[receiverApp.getSysname()] = [];
    }

    messageQueue[receiverApp.getSysname()].push(iacMessage);
  }

  public static sendQueuedMessages(receiverApp: BaseApplication): void {
    if (!(receiverApp instanceof BaseApplication)) {
      throw new Error('The receiver application must be a instance of BaseApplication');
    }

    if (!receiverApp.isIacConnected()) {
      /*
      * The app is still not connect with the IAC. No idea why.
      * But we should not try to send the message again.
      * It's already queued.
      */
      return;
    }
    const msgs = messageQueue[receiverApp.getSysname()] || [];

    msgs.forEach((iacMessage) => this.sendMessage(iacMessage, receiverApp));

    messageQueue[receiverApp.getSysname()] = [];
  }
}
